import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { yellow } from '@mui/material/colors';
import { Trans } from '@lingui/react';
import { i18n } from '@lingui/core';
import { equals } from 'ramda';
import useForm from 'hooks/useForm';
import useDisplayUnits from 'hooks/useDisplayUnits';
import useRoles from 'hooks/useRoles/useRoles';
import { isPcUser, isAdminUser } from 'utils/roles';
import {
  additionalUnitLabels,
  BasicDisplayUnits,
  convertIfRequired,
  getAbbreviation,
  getPressurePumpUnitLabel,
  unitLabels
} from 'hooks/useDisplayUnits/useDisplayUnits';
import {
  flowRateMetricToImperial,
  frictionLossMetricToImperial,
  pumpPressureBarToGravityMetricHead,
  pumpPressureBarToKpa,
  pumpPressureKpaToBar,
  pumpPressureMetricToImperial
} from 'utils/unitConversions';
import { createProjectNumberFormatter, isNumber, round } from 'utils';
import { calculateYieldfromConcentrationByMass } from 'utils/backfill';
import PourPresetField from 'components/PourPresetField';
import { ProjectNumberField } from 'components/Form';
import {
  BackfillRecipeDto,
  BackfillRecipeForm,
  BackfillWarningDto,
  DisplayUnits,
  FillType,
  MetricPressureUnit,
  PourPresetDto,
  PumpType,
  RecipeMutation,
  RheologyDataDto,
  ThroughputControlType,
  UcsCoefficientsDto,
  UnitSystem,
  useCreateRecipeMutation,
  useUpdateRecipeMutation
} from 'providers/api';
import { trackEvent } from 'providers/analytics';
import { FormikHelpers } from 'formik';
import { UseMutationResult } from 'react-query';
import {
  assembleTargetLineWarnings,
  calculateRecipeFromStrength,
  calculateTargetLineResult,
  describeTargetLineWarnings,
  getUCSTargetDays,
  getPointsWithBoosterPressure,
  recomputeScenarioFromMassConcentration
} from '../../../canvas/PCFunctions/PCPasteFunctions';
import { updateRecipeState } from '../../../canvas/Model/updateRecipeState';
import { calculateFriction, calculateFrictionForRoute } from '../../../canvas/Model/updateFriction';
import {
  DictionaryRange,
  NominalOptimisationParams,
  OptimisationErrorType,
  OptimisationTargetLineInput,
  OptimisationTargetLineResult,
  OptimisationTargetType,
  PasteRecipe,
  RecipeState
} from '../../../MineModel/types';
import { Model } from '../../../hooks/useModelState';
import PCRoute from '../../../MineModel/Route/PCRoute';
import {
  generateAllRoutes,
  isRouteActive,
  liveRouteFor,
  routeOptsForProject
} from '../../../MineModel/Route/utils';
import {
  makeDictionaryLimits,
  triggerHydraulicFillStrengthFromRecipe,
  calculateHydraulicOptimise,
  routeFormFields
} from './RecipeFormHelpers';
import AdditionalDisplayUnits from './AdditionalDisplayUnits';
import FormActions from './FormActions';
import HydraulicAdjustedInputs from './HydraulicAdjustedInputs';
import HydraulicTargetInputs from './HydraulicTargetInputs';
import PasteAdjustedInputs from './PasteAdjustedInputs';
import PasteTargetInputs from './PasteTargetInputs';
import RheologyDataSetSelector from './RheologyDataSetSelector';
import NominalParametersSection from './NominalParametersSection';
import TargetLinesSection from './TargetLinesSection';
import UnitSystemNumberField from './UnitSystemNumberField';
import generateRecipeSchema, { recipeSchemaInitialValues } from './RecipeSchema';
import {
  RecipeLimitFormatters,
  formatLimit,
  hydraulicSpecificationForDataset,
  pasteSpecificationForDataset
} from './recipeLimits';
import { PCStope } from '../../../MineModel/Stopes';

type CreateMut = UseMutationResult<string, unknown, RecipeMutation, unknown>;
type UpdateMut = UseMutationResult<void, unknown, RecipeMutation, unknown>;
const PREFIX = 'RecipeForm';

const classes = {
  fullWidthDivider: `${PREFIX}-fullWidthDivider`,
  at: `${PREFIX}-at`,
  form: `${PREFIX}-form`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.fullWidthDivider}`]: {
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.at}`]: {
    width: '100%',
    height: '100%',
    display: 'flex',
    textAlign: 'center',
    justifyContent: 'center',
    alignContent: 'center',
    flexDirection: 'column',
    marginTop: theme.spacing(1),
  },
  [`& .${classes.form}`]: {
    width: '100%',
    '& input[type=number]': {
      '-moz-appearance': 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
}));

const EMPTY_UCS_COEFFICIENTS = {};

const cloneRoute = (sourceRoute: PCRoute): PCRoute => {
  const cloned = Object.create(Object.getPrototypeOf(sourceRoute));
  Object.assign(cloned, sourceRoute);
  cloned.routePoints = sourceRoute.routePoints.map((rp) => ({ ...rp }));
  return cloned;
};

type AutoRecipeFailureReason = 'noRoute' | 'invalidInputs' | 'ucsTooHigh' | 'ucsTooLow' | 'noSolution';

interface AutoRecipeFailure {
  stopeId: string;
  stopeName: string;
  reason: AutoRecipeFailureReason;
}

const failureForStope = (stope: PCStope, reason: AutoRecipeFailureReason): AutoRecipeFailure => ({
  stopeId: stope.data.stopeId,
  stopeName: stope.data.stopeName ?? stope.data.stopeId,
  reason,
});

const optimisationFailureReason = (error: OptimisationErrorType): AutoRecipeFailureReason => {
  if (error === OptimisationErrorType.UCS_TOO_HIGH) return 'ucsTooHigh';
  if (error === OptimisationErrorType.UCS_TOO_LOW) return 'ucsTooLow';
  return 'noSolution';
};

const describeFailureReason = (reason: AutoRecipeFailureReason): string => ({
  noRoute: i18n._('no route to the backfill plant'),
  invalidInputs: i18n._('target inputs are incomplete or invalid'),
  ucsTooHigh: i18n._('target UCS unreachable - try a lower UCS value'),
  ucsTooLow: i18n._('target UCS unreachable - try a higher UCS value'),
  noSolution: i18n._('no valid recipe for the given parameters'),
}[reason]);

interface RecipeFormProps {
  model: Model;
  selectedDataset: RheologyDataDto;
  recipe?: BackfillRecipeDto;
  stopeId: PCStope[];
  stopeName: string[];
  isAutoRecipe: boolean;
  pourPresetGroupId?: string;
  isOptimised: boolean;
  onSuccess?: (recipeId: string) => void | string;
  checkForWarnings: () => BackfillWarningDto[];
  onCancel: () => void;
  onWarning: (warnings: BackfillWarningDto[]) => void;
  updateIsOptimised: (isOptimised: boolean) => void;
}

const RecipeForm = ({
  model,
  selectedDataset,
  recipe,
  stopeId,
  stopeName,
  isAutoRecipe,
  pourPresetGroupId,
  isOptimised,
  onSuccess,
  checkForWarnings,
  onCancel,
  onWarning,
  updateIsOptimised,
}: RecipeFormProps) => {
  const lastManualPastePayloadRef = React.useRef<BackfillRecipeForm | null>(null);
  const lastManualHydraulicPayloadRef = React.useRef<BackfillRecipeForm | null>(null);
  const skipWarningEffectRef = React.useRef(false);
  const initialIsOptimisedState = React.useRef(isOptimised);

  React.useEffect(() => {
    if (isAutoRecipe) return;
    const selected = Object.values(model.routes || {}).find((r) => r.selected);
    // A stored route over an inactive spool can't be kept, so the edited recipe must be re-optimised on a live one.
    if (recipe && selected && !isRouteActive(selected)) {
      initialIsOptimisedState.current = false;
      updateIsOptimised(false);
    }
    const current = selected && isRouteActive(selected) ? selected : undefined;
    if (!current) {
      const routes = model.routes || {};
      if (routes && Object.keys(routes).length > 0) {
        const shortest = liveRouteFor(routes);
        if (shortest) model.setSelectedRoute(shortest);
      }
    }
  }, [model.routes, model.selectedStope?.data.stopeId, model.setSelectedRoute, isAutoRecipe]);

  const selectedRoute = model.getSelectedRoute();
  const throughputControlType = model.project.throughput;
  const activeBackFillPlantNode = model.activeBackfillPlant;
  const projectId = model.project.entityId;
  const metricPressureUnit = model.project.metricPressureUnit ?? MetricPressureUnit.Bar;
  const isKiloPascalPressure = metricPressureUnit === MetricPressureUnit.KiloPascal
    && model.project.unitSystemPreference === UnitSystem.Metric;
  const pumpPressureUnitInfo = model.project.pumpType === PumpType.Pump
    ? getPressurePumpUnitLabel(metricPressureUnit)
    : unitLabels[BasicDisplayUnits.Gravity];
  const toDisplayPumpPressureBar = (bar: number) => (isKiloPascalPressure ? pumpPressureBarToKpa(bar) : bar);
  const isImperial = model.project.unitSystemPreference === UnitSystem.Imperial;
  const flowRateAbbreviation = isImperial
    ? additionalUnitLabels[DisplayUnits.FlowRate].imperial.abbreviation
    : additionalUnitLabels[DisplayUnits.FlowRate].metric.abbreviation;
  const frictionLossAbbreviation = isImperial
    ? additionalUnitLabels[DisplayUnits.FrictionLoss].imperial.abbreviation
    : additionalUnitLabels[DisplayUnits.FrictionLoss].metric.abbreviation;
  const metricPumpPressureTargetAbbreviation = isKiloPascalPressure ? i18n._('kPa') : unitLabels[BasicDisplayUnits.PressurePump].metric.abbreviation;
  const pumpPressureTargetAbbreviation = isImperial
    ? unitLabels[BasicDisplayUnits.PressurePump].imperial.abbreviation
    : metricPumpPressureTargetAbbreviation;
  // Targets store metric (kPa/m, bar); these convert for the header label only.
  const toDisplayFrictionLossTarget = (kPaPerM: number) => (isImperial ? round(frictionLossMetricToImperial(kPaPerM), 3) : kPaPerM);
  const toDisplayPumpPressureTarget = (bar: number) => {
    if (isImperial) return round(pumpPressureMetricToImperial(bar), 1);
    return isKiloPascalPressure ? pumpPressureBarToKpa(bar) : bar;
  };
  const toDisplayFlowRate = (metricFlowRate: number) => (isImperial ? flowRateMetricToImperial(metricFlowRate) : metricFlowRate);
  const pumpPressureDecimalPlaces = model.project.pumpType === PumpType.Pump && isKiloPascalPressure ? 0 : 1;
  const formatLimitNumber = createProjectNumberFormatter(model.project.decimalSeparator, model.project.thousandsSeparator);
  const ucsUnitInfo = unitLabels[BasicDisplayUnits.PressureUCS];
  const { customUnitFields, unitSystemPreference } = model.project;
  const recipeLimitFormatters: RecipeLimitFormatters = {
    ucs: (kPa, bound) => formatLimit(
      convertIfRequired(kPa, unitSystemPreference, ucsUnitInfo, customUnitFields),
      bound,
      getAbbreviation(ucsUnitInfo, unitSystemPreference, customUnitFields),
      formatLimitNumber,
    ),
    pumpPressure: (bar, bound) => formatLimit(
      convertIfRequired(
        model.project.pumpType === PumpType.Pump ? toDisplayPumpPressureBar(bar) : pumpPressureBarToGravityMetricHead(bar),
        unitSystemPreference,
        pumpPressureUnitInfo,
        customUnitFields,
      ),
      bound,
      getAbbreviation(pumpPressureUnitInfo, unitSystemPreference, customUnitFields),
      formatLimitNumber,
    ),
  };
  const [optimisationError, setOptimisationError] = React.useState(OptimisationErrorType.NONE);
  const [autoRecipeFailures, setAutoRecipeFailures] = React.useState<AutoRecipeFailure[]>([]);
  const [selectedRheologyDataset, setSelectedRheologyDataset] = React.useState<RheologyDataDto | undefined>(selectedDataset);
  const [selectedPourPreset, setSelectedPourPreset] = React.useState<PourPresetDto | null>(null);

  const roles = useRoles();
  const isAdminOrPc = isAdminUser(roles) || isPcUser(roles);
  // Everyone may add target lines, but non-P&C/admin users are restricted to flow rate targets,
  // which inherit the optimised recipe's mix and only vary throughput.
  const canEditTargetLines = true;
  const allowedTargetTypes = isAdminOrPc
    ? [OptimisationTargetType.KPaPerM, OptimisationTargetType.PumpPressure, OptimisationTargetType.Yield, OptimisationTargetType.FlowRate]
    : [OptimisationTargetType.FlowRate];
  const userModifiedNominals = React.useRef(false);
  const [nominalParams, setNominalParams] = React.useState<NominalOptimisationParams>(
    recipe?.nominalParameters ?? {
      pipeRatingOffsetPercent: model.project.defaultTransientPercentage ?? undefined,
      minYield: model.project.defaultMinYield ?? undefined,
      maxYield: model.project.defaultMaxYield ?? undefined,
      excludedPipeIds: model.project.defaultExcludedPipeIds as string[] ?? [],
    },
  );
  const handleNominalParamsChange = React.useCallback((params: NominalOptimisationParams) => {
    userModifiedNominals.current = true;
    setNominalParams(params);
  }, []);
  const [targetLineInputs, setTargetLineInputs] = React.useState<OptimisationTargetLineInput[]>(
    recipe?.targetLines?.map((t) => ({
      targetType: t.targetType ?? OptimisationTargetType.KPaPerM,
      kPaPerMTarget: t.kPaPerMTarget ?? undefined,
      targetPipeId: t.targetPipeId ?? undefined,
      pumpPressureTarget: t.pumpPressureTarget ?? undefined,
      yieldTarget: t.yieldTarget ?? undefined,
      flowRateTarget: t.flowRateTarget ?? undefined,
    })) ?? [],
  );
  const [targetLineResults, setTargetLineResults] = React.useState<OptimisationTargetLineResult[]>([]);
  const reconstructedTargetLinesRecipeIdRef = React.useRef<string | undefined>(undefined);

  // Sync nominalParams with project defaults when they become available (new recipes only)
  React.useEffect(() => {
    if (recipe?.nominalParameters || userModifiedNominals.current) return;
    setNominalParams({
      pipeRatingOffsetPercent: model.project.defaultTransientPercentage ?? undefined,
      minYield: model.project.defaultMinYield ?? undefined,
      maxYield: model.project.defaultMaxYield ?? undefined,
      excludedPipeIds: model.project.defaultExcludedPipeIds as string[] ?? [],
    });
  }, [model.project.defaultTransientPercentage, model.project.defaultMinYield, model.project.defaultMaxYield, model.project.defaultExcludedPipeIds]);

  // Clear optimiserResults when recipe changes or when creating a new recipe
  React.useEffect(() => {
    model.setOptimiserResults(undefined);
  }, [recipe?.entityId]);

  // Reconstruct optimiserResults from saved recipe specifications
  React.useEffect(() => {
    if (!recipe || !selectedRoute || model.project.fillType !== FillType.Paste) return;
    if (!recipe.pasteOptimisedSpecification) return;
    if (!selectedRheologyDataset) return;
    if (!model.recipeState) return;

    // Get throughput from recipe specification
    const throughputType = recipe.pasteSpecification?.throughputControlType;
    const throughputValue = recipe.pasteSpecification?.throughput;
    const dryTonnage = throughputType === ThroughputControlType.DryTonnage ? throughputValue : undefined;
    const flowRate = throughputType === ThroughputControlType.FlowRate ? throughputValue : undefined;
    const wetTonnage = throughputType === ThroughputControlType.WetTonnage ? throughputValue : undefined;

    // flowRateOverride is only supplied for flow rate target lines, which run the recipe's mix at
    // their own throughput. It must override the control type as well: updateRecipeState otherwise
    // falls back to model.project.throughput and ignores the substituted flow rate, so the
    // reconstructed target renders at the recipe's throughput — and because flow rate targets
    // inherit the optimised Cw and binder verbatim, that puts the line exactly on top of the
    // optimised one. The sweep-based target types solve at the recipe's throughput and pass nothing.
    const buildPasteRecipe = (
      spec: typeof recipe.pasteOptimisedSpecification,
      baseRoute: PCRoute,
      flowRateOverride?: number,
    ): PasteRecipe | undefined => {
      if (!spec) return undefined;

      const concentrationByMass = spec.massConcentration / 100;
      const cement = spec.binderContent / 100;
      const actualPumpPressureKpa = spec.pumpPressure * 100;

      // Create recipe state for this scenario with throughput values
      const scenarioRecipeState = updateRecipeState(
        model,
        model.recipeState as RecipeState,
        concentrationByMass,
        cement,
        selectedRheologyDataset?.tailingsDrySolidsDensity,
        selectedRheologyDataset?.binderDryParticleDensity,
        selectedRheologyDataset?.carrierFluidDensity,
        dryTonnage,
        flowRateOverride ?? flowRate,
        wetTonnage,
        undefined,
        undefined,
        flowRateOverride ? ThroughputControlType.FlowRate : undefined,
      );

      // Calculate friction for this scenario to get correct route pressures
      calculateFrictionForRoute(model, scenarioRecipeState, baseRoute);

      // Clone the route to preserve these pressure values before next calculation overwrites them
      const clonedRoute = cloneRoute(baseRoute);

      return {
        cement,
        concentrationByMass,
        actualUCSKpa: spec.ucsStrength,
        actualPumpPressureKpa,
        error: OptimisationErrorType.NONE,
        route: clonedRoute,
        slurryDensity: scenarioRecipeState.wetDensity ?? 1000,
      };
    };

    // Use pasteSelectedSpecification as the user may have manually adjusted from optimised
    const selectedSpec = recipe.pasteSelectedSpecification ?? recipe.pasteOptimisedSpecification;
    if (!selectedSpec) return;

    // For selected, the selectedRoute should already have the correct values - just clone it
    const optimisedResult: PasteRecipe = {
      cement: selectedSpec.binderContent / 100,
      concentrationByMass: selectedSpec.massConcentration / 100,
      actualUCSKpa: selectedSpec.ucsStrength,
      actualPumpPressureKpa: selectedSpec.pumpPressure * 100,
      error: OptimisationErrorType.NONE,
      route: cloneRoute(selectedRoute),
      slurryDensity: model.recipeState?.wetDensity ?? 1000,
    };

    // For slack/max, calculate friction then clone
    const slackFlowResult = recipe.pasteSlackFlowSpecification
      ? buildPasteRecipe(recipe.pasteSlackFlowSpecification, cloneRoute(selectedRoute))
      : undefined;
    const maxPressureResult = recipe.pasteMaxPressureSpecification
      ? buildPasteRecipe(recipe.pasteMaxPressureSpecification, cloneRoute(selectedRoute))
      : undefined;

    // Reconstruct target line results from saved recipe
    const savedTargetLineResults: OptimisationTargetLineResult[] | undefined = recipe.targetLines?.length
      ? recipe.targetLines.map((tl) => {
        const tlResult = tl.result
          ? buildPasteRecipe(
            tl.result,
            cloneRoute(selectedRoute),
            tl.targetType === OptimisationTargetType.FlowRate ? (tl.flowRateTarget ?? undefined) : undefined,
          )
          : undefined;
        return {
          targetType: tl.targetType ?? OptimisationTargetType.KPaPerM,
          kPaPerMTarget: tl.kPaPerMTarget ?? undefined,
          targetPipeId: tl.targetPipeId ?? undefined,
          pumpPressureTarget: tl.pumpPressureTarget ?? undefined,
          yieldTarget: tl.yieldTarget ?? undefined,
          flowRateTarget: tl.flowRateTarget ?? undefined,
          result: tlResult,
          hasErrors: tl.hasErrors ?? false,
          errorDescription: tl.errorDescription ?? undefined,
        };
      })
      : undefined;

    // Restore the selected route to the correct values (slack/max/target calculations mutated it)
    calculateFrictionForRoute(model, model.recipeState as RecipeState, selectedRoute);

    model.setOptimiserResults({
      optimisedResult,
      slackFlowResult,
      maxPressureResult,
      targetLineResults: savedTargetLineResults,
    });

    // Seed the target line result blocks once per loaded recipe; re-optimising owns the state from then on
    if (reconstructedTargetLinesRecipeIdRef.current !== recipe.entityId) {
      reconstructedTargetLinesRecipeIdRef.current = recipe.entityId;
      setTargetLineResults(savedTargetLineResults ?? []);
    }
  }, [recipe?.entityId, selectedRoute, selectedRheologyDataset]);
  const maxPumpPressure = selectedRheologyDataset?.pumpPressure.max;
  const ucsCoefficients = selectedRheologyDataset?.ucsCoefficients;
  const hasUcsData = !!ucsCoefficients && Object.keys(ucsCoefficients).length > 0;
  const [limits, setLimits] = React.useState<DictionaryRange>(makeDictionaryLimits(selectedDataset));

  React.useEffect(() => {
    if (!selectedDataset) return;
    if (selectedRheologyDataset && selectedRheologyDataset.rheologyDataSetId !== selectedDataset.rheologyDataSetId) return;
    setSelectedRheologyDataset(selectedDataset);
    setLimits(makeDictionaryLimits(selectedDataset));
  }, [selectedDataset, selectedRheologyDataset]);

  const createMutation = useCreateRecipeMutation();
  const updateMutation = useUpdateRecipeMutation(recipe?.entityId ?? '');
  const mutation = recipe ? updateMutation : createMutation;
  const recipeSchema = generateRecipeSchema(
    model.project.fillType,
    maxPumpPressure ?? 0,
    limits,
    optimisationError,
    ucsCoefficients ?? EMPTY_UCS_COEFFICIENTS,
    hasUcsData,
    recipeLimitFormatters,
  );

  const [formDatas, setFormDatas] = React.useState<BackfillRecipeForm[]>([]);

  const { formik, helpers } = useForm<BackfillRecipeForm>({
    mutation,
    formikConfig: {
      initialValues: recipeSchemaInitialValues(
        selectedRoute?.routePointStrings as string[] ?? [],
        recipe,
        limits,
        7,
        maxPumpPressure ?? 0,
        model.project.fillType,
        selectedRheologyDataset?.rheologyDataSetId ?? '',
        selectedRheologyDataset?.ucsCoefficients ?? EMPTY_UCS_COEFFICIENTS,
        activeBackFillPlantNode?.id ?? undefined,
        selectedRoute?.spoolIds ?? [],
      ),
      onSubmit: async (_recipeModel: BackfillRecipeForm, { setSubmitting }: FormikHelpers<BackfillRecipeForm>) => {
        const dataToSend: BackfillRecipeForm[] = isAutoRecipe
          ? formDatas
          : (() => {
            const v = formik.values;
            const selected = model.getSelectedRoute();
            const plannedRoute = selected && isRouteActive(selected) ? selected : undefined;
            const spoolsStrings = (Array.isArray(v.spools) && v.spools.length > 0) ? v.spools : (plannedRoute?.routePointStrings ?? []);
            const routeSpoolIds = (Array.isArray(v.routeSpools) && v.routeSpools.length > 0) ? v.routeSpools : (plannedRoute?.spoolIds ?? []);

            if (model.project.fillType === FillType.Paste) {
              const pipeFrictionDataItems = (() => {
                const { recipeState } = model;
                const rheology = selectedRheologyDataset;
                if (!recipeState?.wetFlowRate || !recipeState?.totalDryConcentrationByMass || !recipeState?.wetDensity || !rheology) {
                  return undefined;
                }
                return Object.values(model.pipes).map((pipe) => {
                  const velocity = pipe.getVelocity(recipeState.wetFlowRate!);
                  const frictionLoss = calculateFriction(
                    rheology.yieldStressCoefficients.coefficient1,
                    rheology.yieldStressCoefficients.coefficient2,
                    rheology.viscosityCoefficients.coefficient1,
                    rheology.viscosityCoefficients.coefficient2,
                    recipeState.totalDryConcentrationByMass!,
                    pipe.insideDiameter,
                    velocity,
                    recipeState.wetDensity!,
                    rheology.frictionLossFactorOfSafety,
                  );
                  return {
                    pipeName: pipe.name || `Pipe ${pipe.pipeId}`,
                    pipeId: pipe.pipeId,
                    frictionLoss: round(frictionLoss, 2),
                    velocity: round(velocity, 2),
                    insideDiameter: pipe.insideDiameter,
                  };
                });
              })();

              return [{
                pasteOptimisedSpecification: {
                  massConcentration: v.pasteOptimisedSpecification?.massConcentration ?? 0,
                  binderContent: v.pasteOptimisedSpecification?.binderContent ?? 0,
                  ucsStrength: v.pasteOptimisedSpecification?.ucsStrength ?? 0,
                  pumpPressure: v.pasteOptimisedSpecification?.pumpPressure ?? 0,
                },
                pasteSelectedSpecification: {
                  massConcentration: v.pasteSelectedSpecification?.massConcentration
                    ?? v.pasteOptimisedSpecification?.massConcentration ?? 0,
                  binderContent: v.pasteSelectedSpecification?.binderContent
                    ?? v.pasteOptimisedSpecification?.binderContent ?? 0,
                  ucsStrength: v.pasteSelectedSpecification?.ucsStrength
                    ?? v.pasteOptimisedSpecification?.ucsStrength ?? 0,
                  pumpPressure: v.pasteSelectedSpecification?.pumpPressure
                    ?? v.pasteOptimisedSpecification?.pumpPressure ?? 0,
                },
                pasteSlackFlowSpecification: v.pasteSlackFlowSpecification,
                pasteMaxPressureSpecification: v.pasteMaxPressureSpecification,
                pasteSpecification: {
                  pumpPressure: v.pasteSpecification?.pumpPressure ?? limits.pumpPressure.default ?? 0,
                  targetDays: v.pasteSpecification?.targetDays || getUCSTargetDays(ucsCoefficients ?? {}),
                  targetUCSStrength: v.pasteSpecification?.targetUCSStrength ?? 0,
                  throughput: v.pasteSpecification?.throughput ?? limits.tons.default ?? limits.tons.min,
                  throughputControlType,
                },
                backfillPlantId: v.backfillPlantId || '',
                isAutoRecipe: false,
                stopeId: stopeId[0]?.data.stopeId,
                reference: v.reference,
                rheologyDataSetId: v.rheologyDataSetId,
                warnings: v?.warnings ?? [],
                spools: spoolsStrings,
                routeSpools: routeSpoolIds,
                pipeFrictionDataItems,
                nominalParameters: (nominalParams.minYield !== undefined || nominalParams.maxYield !== undefined
                  || nominalParams.pipeRatingOffsetPercent !== undefined)
                  ? nominalParams : undefined,
                targetLines: canEditTargetLines && targetLineInputs.length > 0
                  ? targetLineInputs.map((input, idx) => ({
                    targetType: input.targetType,
                    kPaPerMTarget: input.kPaPerMTarget,
                    targetPipeId: input.targetPipeId,
                    pumpPressureTarget: input.pumpPressureTarget,
                    yieldTarget: input.yieldTarget,
                    flowRateTarget: input.flowRateTarget,
                    result: targetLineResults[idx]?.result ? {
                      massConcentration: round(targetLineResults[idx].result!.concentrationByMass * 100),
                      binderContent: round(targetLineResults[idx].result!.cement * 100),
                      ucsStrength: targetLineResults[idx].result!.actualUCSKpa,
                      pumpPressure: pumpPressureKpaToBar(targetLineResults[idx].result!.actualPumpPressureKpa),
                    } : undefined,
                    hasErrors: targetLineResults[idx]?.hasErrors ?? false,
                    errorDescription: targetLineResults[idx]?.errorDescription,
                  })) : undefined,
              }];
            }

            return [{
              hydraulicSpecification: {
                binderTailingsRatio: v.hydraulicSpecification?.binderTailingsRatio ?? 0.02,
                throughput: v.hydraulicSpecification?.throughput ?? limits.tons.default as number,
                throughputControlType,
              },
              hydraulicOptimisedSpecification: {
                massConcentration: v.hydraulicOptimisedSpecification?.massConcentration ?? 0,
                settlingVelocity: v.hydraulicOptimisedSpecification?.settlingVelocity ?? 0,
                minVelocity: v.hydraulicOptimisedSpecification?.minVelocity ?? 0,
                maxPipePressure: v.hydraulicOptimisedSpecification?.maxPipePressure ?? 0,
              },
              hydraulicSelectedSpecification: {
                massConcentration: v.hydraulicSelectedSpecification?.massConcentration
                  ?? v.hydraulicOptimisedSpecification?.massConcentration ?? 0,
                settlingVelocity: v.hydraulicSelectedSpecification?.settlingVelocity
                  ?? v.hydraulicOptimisedSpecification?.settlingVelocity ?? 0,
                minVelocity: v.hydraulicSelectedSpecification?.minVelocity
                  ?? v.hydraulicOptimisedSpecification?.minVelocity ?? 0,
                maxPipePressure: v.hydraulicSelectedSpecification?.maxPipePressure
                  ?? v.hydraulicOptimisedSpecification?.maxPipePressure ?? 0,
              },
              backfillPlantId: v.backfillPlantId || '',
              isAutoRecipe: false,
              stopeId: stopeId[0]?.data.stopeId,
              reference: v.reference,
              rheologyDataSetId: v.rheologyDataSetId,
              warnings: v?.warnings ?? [],
              spools: spoolsStrings,
              routeSpools: routeSpoolIds,
            }];
          })();

        const submittedStopeNames = isAutoRecipe
          ? dataToSend
            .map((form) => stopeId.find((stope) => stope.data.stopeId === form.stopeId))
            .map((stope) => stope?.data.stopeName ?? stope?.data.stopeId)
            .filter((name): name is string => !!name)
          : stopeName;

        const payload: RecipeMutation = {
          projectId,
          recipeId: recipe?.entityId ?? null,
          data: dataToSend,
          context: { stopeName: submittedStopeNames },
        };

        setSubmitting(true);
        try {
          if (!mutation) return;

          if (recipe?.entityId) {
            await (mutation as UpdateMut).mutateAsync(payload);
            const recipeId = recipe.entityId;
            if (onSuccess) onSuccess(recipeId);
          } else {
            const result = await (mutation as CreateMut).mutateAsync(payload);
            const createdRecipeId = Array.isArray(result) ? result[0] : result;
            if (onSuccess) onSuccess(createdRecipeId || '');
          }

          trackEvent('recipe_saved', { project_id: projectId });

          lastManualPastePayloadRef.current = null;
          lastManualHydraulicPayloadRef.current = null;
        } finally {
          setSubmitting(false);
        }
      },
      validationSchema: recipeSchema,
      validateOnMount: true,
    },
  });

  const {
    values,
    isValid,
    isSubmitting,
    dirty,
    resetForm,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
  } = formik;

  // Recalculate slack/max when route changes (keep optimised values, only recalculate min/max)
  const routeKey = selectedRoute?.routePointStrings?.join(',') ?? '';
  const hasOptimisedResult = !!model.optimiserResults?.optimisedResult;
  const prevRouteKeyRef = React.useRef<string>('');

  React.useEffect(() => {
    if (!selectedRoute || model.project.fillType !== FillType.Paste) return;
    if (!hasOptimisedResult) return;
    if (!selectedRheologyDataset || !model.recipeState) return;

    if (routeKey === prevRouteKeyRef.current) return;
    prevRouteKeyRef.current = routeKey;

    const existingOptimisedResult = model.optimiserResults?.optimisedResult;
    if (!existingOptimisedResult) return;

    const { concentrationByMass, cement } = existingOptimisedResult;

    const throughputValue = values.pasteSpecification?.throughput ?? 0;
    const initialRecipeState: RecipeState = {
      ...(model.recipeState ?? {
        totalDryConcentrationByMass: concentrationByMass,
        binderConcentrationByMass: cement,
        tailingsSolidsDensity: selectedRheologyDataset?.tailingsDrySolidsDensity,
        binderParticleDensity: selectedRheologyDataset?.binderDryParticleDensity,
        carrierFluidDensity: selectedRheologyDataset?.carrierFluidDensity,
        throughputControlType,
        tailingsDryTonnage: throughputControlType === ThroughputControlType.DryTonnage ? throughputValue : undefined,
        wetTonnage: throughputControlType === ThroughputControlType.WetTonnage ? throughputValue : undefined,
        wetFlowRate: throughputControlType === ThroughputControlType.FlowRate ? throughputValue : undefined,
      }),
      rheologyDataSetId: selectedRheologyDataset?.rheologyDataSetId,
    };

    const optimisedRecipeState = updateRecipeState(
      model,
      initialRecipeState,
      concentrationByMass,
      cement,
      selectedRheologyDataset?.tailingsDrySolidsDensity,
      selectedRheologyDataset?.binderDryParticleDensity,
      selectedRheologyDataset?.carrierFluidDensity,
      throughputControlType === ThroughputControlType.DryTonnage ? throughputValue : undefined,
      throughputControlType === ThroughputControlType.FlowRate ? throughputValue : undefined,
      throughputControlType === ThroughputControlType.WetTonnage ? throughputValue : undefined,
    );
    const routeForCalculation = cloneRoute(selectedRoute);

    const recalculatedRoute = calculateFrictionForRoute(model, optimisedRecipeState, routeForCalculation);

    const result = calculateRecipeFromStrength(
      model,
      selectedRheologyDataset as RheologyDataDto,
      values.pasteSpecification?.targetUCSStrength || 0,
      values.pasteSpecification?.targetDays || getUCSTargetDays(ucsCoefficients as Record<number, UcsCoefficientsDto>),
      values.pasteSpecification?.pumpPressure ?? 0,
      values.pasteSpecification?.throughput ?? 0,
      selectedRoute,
      throughputControlType,
      nominalParams,
    );

    // Set recipeState AFTER the solver (solver's loop overwrites recipeState without pipeRatingOffsetPercent)
    model.setRecipeState({ ...optimisedRecipeState, pipeRatingOffsetPercent: nominalParams.pipeRatingOffsetPercent });

    if (existingOptimisedResult && recalculatedRoute) {
      model.setOptimiserResults({
        optimisedResult: {
          ...existingOptimisedResult,
          route: cloneRoute(recalculatedRoute),
        },
        slackFlowResult: result.slackFlowResult,
        maxPressureResult: result.maxPressureResult,
        targetLineResults: model.optimiserResults?.targetLineResults,
      });

      model.setSelectedRoute(recalculatedRoute);
    }

    if (result.slackFlowResult) {
      setFieldValue('pasteSlackFlowSpecification', {
        massConcentration: round(result.slackFlowResult.concentrationByMass * 100),
        binderContent: round(result.slackFlowResult.cement * 100),
        ucsStrength: result.slackFlowResult.actualUCSKpa,
        pumpPressure: pumpPressureKpaToBar(result.slackFlowResult.actualPumpPressureKpa),
      }, false);
    } else {
      setFieldValue('pasteSlackFlowSpecification', undefined, false);
    }

    if (result.maxPressureResult) {
      setFieldValue('pasteMaxPressureSpecification', {
        massConcentration: round(result.maxPressureResult.concentrationByMass * 100),
        binderContent: round(result.maxPressureResult.cement * 100),
        ucsStrength: result.maxPressureResult.actualUCSKpa,
        pumpPressure: pumpPressureKpaToBar(result.maxPressureResult.actualPumpPressureKpa),
      }, false);
    } else {
      setFieldValue('pasteMaxPressureSpecification', undefined, false);
    }
  }, [routeKey, hasOptimisedResult]);

  const handleReset = () => {
    mutation?.reset();
    updateIsOptimised(false);
    setOptimisationError(OptimisationErrorType.NONE);
    setAutoRecipeFailures([]);
    setFormDatas([]);
    setFieldValue('pasteSpecification.targetUCSStrength', values.pasteSpecification?.targetUCSStrength ?? 0);
  };

  // Recomputes a scenario (slack flow / max pressure / target line) for a manually entered mass concentration.
  // The scenario calculation displaces pipe frictions and the route pressures, so the selected route is restored afterwards.
  const recomputeScenario = (massConcentration: number, flowRateTarget?: number): PasteRecipe | undefined => {
    if (!selectedRheologyDataset || !selectedRoute || !model.recipeState) return undefined;
    const recomputed = recomputeScenarioFromMassConcentration({
      model,
      rheologyData: selectedRheologyDataset,
      route: selectedRoute,
      massConcentration,
      targetUCS: values.pasteSpecification?.targetUCSStrength ?? 0,
      curingDays: values.pasteSpecification?.targetDays ?? 0,
      throughput: flowRateTarget ?? values.pasteSpecification?.throughput ?? 0,
      throughputControlType: flowRateTarget === undefined ? throughputControlType : ThroughputControlType.FlowRate,
    });
    calculateFrictionForRoute(model, model.recipeState as RecipeState, selectedRoute);
    return recomputed;
  };

  const handleScenarioMassConcentrationChange = (
    field: 'pasteSlackFlowSpecification' | 'pasteMaxPressureSpecification',
    massConcentrationPercent: number | undefined,
  ) => {
    if (!isNumber(massConcentrationPercent)) return;
    const recomputed = recomputeScenario(massConcentrationPercent / 100);
    if (!recomputed) return;

    setFieldValue(field, {
      massConcentration: massConcentrationPercent,
      binderContent: round(recomputed.cement * 100),
      ucsStrength: recomputed.actualUCSKpa,
      pumpPressure: pumpPressureKpaToBar(recomputed.actualPumpPressureKpa),
    });

    const existingResults = model.optimiserResults;
    if (!existingResults) return;
    model.setOptimiserResults(field === 'pasteSlackFlowSpecification'
      ? { ...existingResults, slackFlowResult: recomputed }
      : { ...existingResults, maxPressureResult: recomputed });
  };

  const handleTargetLineMassConcentrationChange = (index: number, massConcentrationPercent: number | undefined) => {
    if (!isNumber(massConcentrationPercent)) return;
    const clampedPercent = Math.min(
      Math.max(massConcentrationPercent, limits.concentration.min * 100),
      limits.concentration.max * 100,
    );
    // A flow rate target line runs in flow rate control mode at its own flow rate, not the recipe's throughput.
    const targetInput = targetLineInputs[index];
    const flowRateTarget = targetInput?.targetType === OptimisationTargetType.FlowRate ? targetInput.flowRateTarget : undefined;
    const recomputed = recomputeScenario(clampedPercent / 100, flowRateTarget);
    if (!recomputed) return;

    // The optimiser's OP/SF/BP/OPP codes describe the concentration it picked, so re-derive them for the edited one.
    const warnings = describeTargetLineWarnings(assembleTargetLineWarnings(
      model,
      recomputed.route,
      recomputed.actualPumpPressureKpa,
      values.pasteSpecification?.pumpPressure ?? limits.pumpPressure.default ?? 0,
      recomputed.slurryDensity,
    ));

    setTargetLineResults((previous) => previous.map((line, i) => (i === index ? { ...line, result: recomputed, ...warnings } : line)));

    const existingResults = model.optimiserResults;
    if (!existingResults?.targetLineResults) return;
    model.setOptimiserResults({
      ...existingResults,
      targetLineResults: existingResults.targetLineResults.map((line, i) => (i === index ? { ...line, result: recomputed, ...warnings } : line)),
    });
  };

  // Flow rate targets inherit the adjusted recipe's mix, so they must follow the Adjusted
  // Concentration slider — not only the optimise run that first produced them. Sweep-based targets
  // solved for their own concentration and are left alone. Deps are the adjusted spec only; the
  // effect never writes them back, so it cannot loop.
  const adjustedMassConcentration = values.pasteSelectedSpecification?.massConcentration;
  const adjustedBinderContent = values.pasteSelectedSpecification?.binderContent;
  const adjustedUcsStrength = values.pasteSelectedSpecification?.ucsStrength;

  React.useEffect(() => {
    if (!selectedRheologyDataset || !selectedRoute || !model.recipeState) return;
    if (!isNumber(adjustedMassConcentration) || !isNumber(adjustedBinderContent)) return;
    if (!targetLineInputs.some((input) => input.targetType === OptimisationTargetType.FlowRate)) return;

    const optimisedSpecificationForTargets = {
      concentrationByMass: adjustedMassConcentration / 100,
      cement: adjustedBinderContent / 100,
      actualUCSKpa: adjustedUcsStrength ?? 0,
    };

    // calculateTargetLineResult clones internally on the FlowRate branch, so selectedRoute is safe here.
    const recomputeFlowRateTargets = (lines: OptimisationTargetLineResult[]) => lines.map((line, index) => {
      const input = targetLineInputs[index];
      if (input?.targetType !== OptimisationTargetType.FlowRate) return line;
      return calculateTargetLineResult(
        model,
        selectedRheologyDataset,
        input,
        values.pasteSpecification?.pumpPressure ?? limits.pumpPressure.default ?? 0,
        values.pasteSpecification?.throughput ?? limits.tons.default ?? 0,
        selectedRoute,
        throughputControlType,
        values.pasteSpecification?.targetDays || getUCSTargetDays(ucsCoefficients as Record<number, UcsCoefficientsDto>),
        values.pasteSpecification?.targetUCSStrength || 0,
        nominalParams,
        optimisedSpecificationForTargets,
      );
    });

    setTargetLineResults((previous) => (previous.length > 0 ? recomputeFlowRateTargets(previous) : previous));

    const existingResults = model.optimiserResults;
    if (!existingResults?.targetLineResults?.length) return;
    model.setOptimiserResults({
      ...existingResults,
      targetLineResults: recomputeFlowRateTargets(existingResults.targetLineResults),
    });
  }, [adjustedMassConcentration, adjustedBinderContent, adjustedUcsStrength]);

  const handleRheologyDatasetSelected = (newRheologyData: RheologyDataDto | undefined) => {
    setSelectedRheologyDataset(newRheologyData);
    if (newRheologyData) {
      setLimits(makeDictionaryLimits(newRheologyData));
      // Unvalidated here: the rheologyDataSetId effect revalidates once the new dataset's schema has rendered.
      if (model.project.fillType === FillType.Paste) {
        setFieldValue('pasteSpecification', pasteSpecificationForDataset(values.pasteSpecification, newRheologyData, !!recipe), false);
      } else {
        setFieldValue('hydraulicSpecification', hydraulicSpecificationForDataset(values.hydraulicSpecification, newRheologyData, !!recipe), false);
      }
    }
    if (model.recipeState) {
      model.setRecipeState({
        ...model.recipeState,
        rheologyDataSetId: newRheologyData?.rheologyDataSetId,
      });
    }
  };

  const handlePourPresetSelected = (selectedPreset: PourPresetDto | null) => {
    setSelectedPourPreset(selectedPreset);
    const newRheologyDataset = model.rheology?.find((rheologyDataset) => rheologyDataset.rheologyDataSetId === selectedPreset?.rheologyDatasetId);
    newRheologyDataset && handleRheologyDatasetSelected(newRheologyDataset);
  };
  const canOptimiseAuto = (hasInputs: boolean) => hasInputs;
  const canOptimiseManual = (hasInputs: boolean) => dirty && hasInputs;
  const handlePasteOptimise = () => {
    setOptimisationError(OptimisationErrorType.NONE);
    // Without this a stope that optimised on an earlier run and fails on this one still gets created.
    setFormDatas([]);

    const failures = stopeId.map((stope): AutoRecipeFailure | undefined => {
      if (!model.activeBackfillPlant || !model.spools || !model.pipes || !model.nodes || !stope?.originNode) {
        return failureForStope(stope, 'noRoute');
      }

      let routes = model.routes || {};

      if (Object.keys(model.routes).length === 0 || Object.values(model.routes)[0].stope.data.stopeId !== stope.data.stopeId) {
        try {
          ({ routeObjects: routes } = generateAllRoutes(
            model.activeBackfillPlant,
            model.spools,
            stope,
            Object.values(model.pipes),
            routeOptsForProject(model.project),
            model.nodes,
            model.pipes,
          ));
        } catch {
          return failureForStope(stope, 'noRoute');
        }
      }

      const liveRoute = liveRouteFor(routes);
      if (!liveRoute) return failureForStope(stope, 'noRoute');
      const prevRoute = model.getSelectedRoute?.();
      model.setSelectedRoute?.(liveRoute);

      const hasInputs = !!selectedRheologyDataset && !!limits && isValid;
      const canRun = isAutoRecipe ? canOptimiseAuto(hasInputs) : canOptimiseManual(hasInputs);
      if (!canRun) {
        if (isAutoRecipe && prevRoute !== undefined) model.setSelectedRoute?.(prevRoute);
        return failureForStope(stope, 'invalidInputs');
      }

      setFieldValue('pasteSpecification.pumpPressure', values.pasteSpecification?.pumpPressure ?? limits.pumpPressure.default ?? 0);
      setFieldValue('pasteSpecification.throughput', values.pasteSpecification?.throughput || limits.tons.default || limits.tons.min);

      const result = calculateRecipeFromStrength(
        model,
        selectedRheologyDataset as RheologyDataDto,
        values.pasteSpecification?.targetUCSStrength || 0,
        values.pasteSpecification?.targetDays || getUCSTargetDays(ucsCoefficients as Record<number, UcsCoefficientsDto>),
        values.pasteSpecification?.pumpPressure ?? limits.pumpPressure.default ?? 0,
        values.pasteSpecification?.throughput ?? limits.tons.default ?? 0,
        cloneRoute(liveRoute as PCRoute),
        throughputControlType,
        nominalParams,
      );

      if (result.optimisedResult.error !== OptimisationErrorType.NONE) {
        // A form-level error lands on the shared Target UCS field, blocking creation of the stopes that did optimise.
        if (!isAutoRecipe) {
          setOptimisationError(result.optimisedResult.error);
          setFieldTouched('pasteSpecification.targetUCSStrength', true, false);
        }
        if (isAutoRecipe && prevRoute !== undefined) model.setSelectedRoute?.(prevRoute);
        return failureForStope(stope, optimisationFailureReason(result.optimisedResult.error));
      }

      // Flow rate targets inherit this mix verbatim, so they move whenever the optimised recipe does.
      const optimisedSpecificationForTargets = {
        concentrationByMass: result.optimisedResult.concentrationByMass,
        cement: result.optimisedResult.cement,
        actualUCSKpa: result.optimisedResult.actualUCSKpa,
      };

      const computedTargetLineResults: OptimisationTargetLineResult[] = canEditTargetLines && targetLineInputs.length > 0
        ? targetLineInputs.map((targetInput) => calculateTargetLineResult(
          model,
          selectedRheologyDataset as RheologyDataDto,
          targetInput,
          values.pasteSpecification?.pumpPressure ?? limits.pumpPressure.default ?? 0,
          values.pasteSpecification?.throughput ?? limits.tons.default ?? 0,
          liveRoute as PCRoute,
          throughputControlType,
          values.pasteSpecification?.targetDays || getUCSTargetDays(ucsCoefficients as Record<number, UcsCoefficientsDto>),
          values.pasteSpecification?.targetUCSStrength || 0,
          nominalParams,
          optimisedSpecificationForTargets,
        ))
        : [];
      setTargetLineResults(computedTargetLineResults);

      model.setOptimiserResults({
        ...result,
        targetLineResults: computedTargetLineResults.length > 0 ? computedTargetLineResults : undefined,
      });

      const { cement, concentrationByMass, actualUCSKpa, actualPumpPressureKpa } = result.optimisedResult;
      const binderContent = round(cement * 100);
      const massConcentration = round(concentrationByMass * 100);
      const pumpPressureBar = pumpPressureKpaToBar(actualPumpPressureKpa);

      setFieldValue('spools', liveRoute.routePointStrings ?? [], false);
      setFieldValue('routeSpools', liveRoute.spoolIds, false);

      const throughputValue = values.pasteSpecification?.throughput ?? limits.tons.default ?? 0;
      const initialRecipeState: RecipeState = {
        ...(model.recipeState ?? {
          totalDryConcentrationByMass: concentrationByMass,
          binderConcentrationByMass: cement,
          tailingsSolidsDensity: selectedRheologyDataset?.tailingsDrySolidsDensity,
          binderParticleDensity: selectedRheologyDataset?.binderDryParticleDensity,
          carrierFluidDensity: selectedRheologyDataset?.carrierFluidDensity,
          throughputControlType,
          tailingsDryTonnage: throughputControlType === ThroughputControlType.DryTonnage ? throughputValue : undefined,
          wetTonnage: throughputControlType === ThroughputControlType.WetTonnage ? throughputValue : undefined,
          wetFlowRate: throughputControlType === ThroughputControlType.FlowRate ? throughputValue : undefined,
        }),
        rheologyDataSetId: selectedRheologyDataset?.rheologyDataSetId,
      };

      const optimisedRecipeState = updateRecipeState(
        model,
        initialRecipeState,
        concentrationByMass,
        cement,
        selectedRheologyDataset?.tailingsDrySolidsDensity,
        selectedRheologyDataset?.binderDryParticleDensity,
        selectedRheologyDataset?.carrierFluidDensity,
        throughputControlType === ThroughputControlType.DryTonnage ? throughputValue : undefined,
        throughputControlType === ThroughputControlType.FlowRate ? throughputValue : undefined,
        throughputControlType === ThroughputControlType.WetTonnage ? throughputValue : undefined,
      );
      model.setRecipeState({ ...optimisedRecipeState, pipeRatingOffsetPercent: nominalParams.pipeRatingOffsetPercent });

      const recalculatedRoute = calculateFrictionForRoute(model, optimisedRecipeState, liveRoute);
      model.setSelectedRoute(recalculatedRoute);

      const finalPumpPressureBar = recalculatedRoute?.pumpPressureKpa ? pumpPressureKpaToBar(recalculatedRoute.pumpPressureKpa) : pumpPressureBar;

      setFieldValue('pasteOptimisedSpecification.binderContent', binderContent, false);
      setFieldValue('pasteSelectedSpecification.binderContent', binderContent, false);

      setFieldValue('pasteOptimisedSpecification.massConcentration', massConcentration, false);
      setFieldValue('pasteSelectedSpecification.massConcentration', massConcentration, false);

      setFieldValue('pasteOptimisedSpecification.ucsStrength', actualUCSKpa, false);
      setFieldValue('pasteSelectedSpecification.ucsStrength', actualUCSKpa, false);

      setFieldValue('pasteOptimisedSpecification.pumpPressure', finalPumpPressureBar, false);
      setFieldValue('pasteSelectedSpecification.pumpPressure', finalPumpPressureBar, false);

      if (result.slackFlowResult) {
        setFieldValue('pasteSlackFlowSpecification', {
          massConcentration: round(result.slackFlowResult.concentrationByMass * 100),
          binderContent: round(result.slackFlowResult.cement * 100),
          ucsStrength: result.slackFlowResult.actualUCSKpa,
          pumpPressure: pumpPressureKpaToBar(result.slackFlowResult.actualPumpPressureKpa),
        }, false);
      } else {
        setFieldValue('pasteSlackFlowSpecification', undefined, false);
      }

      if (result.maxPressureResult) {
        setFieldValue('pasteMaxPressureSpecification', {
          massConcentration: round(result.maxPressureResult.concentrationByMass * 100),
          binderContent: round(result.maxPressureResult.cement * 100),
          ucsStrength: result.maxPressureResult.actualUCSKpa,
          pumpPressure: pumpPressureKpaToBar(result.maxPressureResult.actualPumpPressureKpa),
        }, false);
      } else {
        setFieldValue('pasteMaxPressureSpecification', undefined, false);
      }

      const routeWarnings: BackfillWarningDto[] = recalculatedRoute.warnings ?? [];
      const computedWarnings = (values.pasteSpecification?.targetUCSStrength || 0) > actualUCSKpa
        ? [...routeWarnings, { code: 'FormUcsStrengthBelowTarget', display: i18n._('UCS STRENGTH BELOW TARGET') }]
        : routeWarnings;
      onWarning(computedWarnings);
      setFieldValue('warnings', computedWarnings, false);
      skipWarningEffectRef.current = true;

      const updatedFormData: BackfillRecipeForm = {
        pasteOptimisedSpecification: {
          massConcentration,
          binderContent,
          ucsStrength: actualUCSKpa,
          pumpPressure: finalPumpPressureBar,
        },
        pasteSelectedSpecification: {
          massConcentration,
          binderContent,
          ucsStrength: actualUCSKpa,
          pumpPressure: finalPumpPressureBar,
        },
        pasteSlackFlowSpecification: result.slackFlowResult
          ? {
              massConcentration: round(result.slackFlowResult.concentrationByMass * 100),
              binderContent: round(result.slackFlowResult.cement * 100),
              ucsStrength: result.slackFlowResult.actualUCSKpa,
              pumpPressure: pumpPressureKpaToBar(result.slackFlowResult.actualPumpPressureKpa),
            }
          : undefined,
        pasteMaxPressureSpecification: result.maxPressureResult
          ? {
              massConcentration: round(result.maxPressureResult.concentrationByMass * 100),
              binderContent: round(result.maxPressureResult.cement * 100),
              ucsStrength: result.maxPressureResult.actualUCSKpa,
              pumpPressure: pumpPressureKpaToBar(result.maxPressureResult.actualPumpPressureKpa),
            }
          : undefined,
        pasteSpecification: {
          pumpPressure: values.pasteSpecification?.pumpPressure ?? limits.pumpPressure.default ?? 0,
          targetDays: values.pasteSpecification?.targetDays || getUCSTargetDays(ucsCoefficients as Record<number, UcsCoefficientsDto>),
          targetUCSStrength: values.pasteSpecification?.targetUCSStrength || 0,
          throughput: values.pasteSpecification?.throughput || limits.tons.default || limits.tons.min,
          throughputControlType,
        },
        backfillPlantId: values.backfillPlantId || '',
        isAutoRecipe,
        stopeId: stope.data.stopeId,
        reference: values.reference,
        rheologyDataSetId: values.rheologyDataSetId,
        warnings: computedWarnings,
        ...routeFormFields(liveRoute),
        nominalParameters: isAdminOrPc && (nominalParams.minYield !== undefined || nominalParams.maxYield !== undefined
          || nominalParams.pipeRatingOffsetPercent !== undefined)
          ? nominalParams : undefined,
        targetLines: canEditTargetLines && computedTargetLineResults.length > 0
          ? targetLineInputs.map((input, idx) => ({
            targetType: input.targetType,
            kPaPerMTarget: input.kPaPerMTarget,
            targetPipeId: input.targetPipeId,
            pumpPressureTarget: input.pumpPressureTarget,
            yieldTarget: input.yieldTarget,
            flowRateTarget: input.flowRateTarget,
            result: computedTargetLineResults[idx]?.result ? {
              massConcentration: round(computedTargetLineResults[idx].result!.concentrationByMass * 100),
              binderContent: round(computedTargetLineResults[idx].result!.cement * 100),
              ucsStrength: computedTargetLineResults[idx].result!.actualUCSKpa,
              pumpPressure: pumpPressureKpaToBar(computedTargetLineResults[idx].result!.actualPumpPressureKpa),
            } : undefined,
            hasErrors: computedTargetLineResults[idx]?.hasErrors ?? false,
            errorDescription: computedTargetLineResults[idx]?.errorDescription,
          })) : undefined,
      };

      updateIsOptimised(true);

      if (isAutoRecipe) {
        setFormDatas((prev) => [
          ...prev.filter((f) => f.stopeId !== updatedFormData.stopeId),
          updatedFormData,
        ]);
        if (prevRoute !== undefined) model.setSelectedRoute?.(prevRoute);
      }

      mutation?.reset();
      return undefined;
    }).filter((failure): failure is AutoRecipeFailure => !!failure);

    setAutoRecipeFailures(failures);
  };

  const handleHydraulicOptimise = () => {
    setFormDatas([]);

    const failures = stopeId.map((stope): AutoRecipeFailure | undefined => {
      if (!model.activeBackfillPlant || !model.spools || !model.pipes || !model.nodes || !stope?.originNode) {
        return failureForStope(stope, 'noRoute');
      }

      let routes = model.routes || {};

      if (Object.keys(model.routes).length === 0 || Object.values(model.routes)[0].stope.data.stopeId !== stope.data.stopeId) {
        try {
          ({ routeObjects: routes } = generateAllRoutes(
            model.activeBackfillPlant,
            model.spools,
            stope,
            Object.values(model.pipes),
            routeOptsForProject(model.project),
            model.nodes,
            model.pipes,
          ));
        } catch {
          return failureForStope(stope, 'noRoute');
        }
      }

      const liveRoute = liveRouteFor(routes);
      if (!liveRoute) return failureForStope(stope, 'noRoute');
      const prevRoute = model.getSelectedRoute?.();
      model.setSelectedRoute?.(liveRoute);

      const hasInputs = !!selectedRheologyDataset && !!limits && isValid;
      const canRun = isAutoRecipe ? canOptimiseAuto(hasInputs) : canOptimiseManual(hasInputs);
      if (!canRun) {
        if (isAutoRecipe && prevRoute !== undefined) model.setSelectedRoute?.(prevRoute);
        return failureForStope(stope, 'invalidInputs');
      }
      const results = triggerHydraulicFillStrengthFromRecipe(
        model,
        values.hydraulicSpecification?.binderTailingsRatio || 0.02,
        values.hydraulicSelectedSpecification?.massConcentration || limits.concentration.min,
        selectedRheologyDataset?.tailingsDrySolidsDensity ?? 0,
        selectedRheologyDataset?.binderDryParticleDensity ?? 0,
        selectedRheologyDataset?.carrierFluidDensity ?? 0,
        values.hydraulicSpecification?.throughput ?? limits.tons.default,
      );
      if (!results) {
        if (isAutoRecipe && prevRoute !== undefined) model.setSelectedRoute?.(prevRoute);
        return failureForStope(stope, 'noSolution');
      }

      const { concentrationByMass } = calculateHydraulicOptimise(
        model,
        values.hydraulicSpecification?.binderTailingsRatio || 0.02,
        throughputControlType,
        values.hydraulicSpecification?.throughput ?? limits.tons.default,
      );

      setFieldValue('hydraulicOptimisedSpecification.settlingVelocity', results.settlingVelocity);
      setFieldValue('hydraulicOptimisedSpecification.minVelocity', results.minVelocity);
      setFieldValue('hydraulicOptimisedSpecification.maxPipePressure', results.maxPipePressure);
      setFieldValue('hydraulicSelectedSpecification.settlingVelocity', results.settlingVelocity);
      setFieldValue('hydraulicSelectedSpecification.minVelocity', results.minVelocity);
      setFieldValue('hydraulicSelectedSpecification.maxPipePressure', results.maxPipePressure);
      setFieldValue('hydraulicOptimisedSpecification.massConcentration', concentrationByMass);
      setFieldValue('hydraulicSelectedSpecification.massConcentration', concentrationByMass);

      const updatedFormData: BackfillRecipeForm = {
        hydraulicSpecification: {
          binderTailingsRatio: values.hydraulicSpecification?.binderTailingsRatio || 0.02,
          throughput: values.hydraulicSpecification?.throughput ?? limits.tons.default as number,
          throughputControlType,
        },
        hydraulicOptimisedSpecification: {
          massConcentration: concentrationByMass,
          settlingVelocity: results.settlingVelocity,
          minVelocity: results.minVelocity,
          maxPipePressure: results.maxPipePressure,
        },
        hydraulicSelectedSpecification: {
          massConcentration: concentrationByMass,
          settlingVelocity: results.settlingVelocity,
          minVelocity: results.minVelocity,
          maxPipePressure: results.maxPipePressure,
        },

        backfillPlantId: values.backfillPlantId || '',
        isAutoRecipe,
        stopeId: stope.data.stopeId,
        reference: values.reference,
        rheologyDataSetId: values.rheologyDataSetId,
        warnings: values?.warnings ?? [],
        ...routeFormFields(liveRoute),
      };

      updateIsOptimised(true);

      if (isAutoRecipe) {
        setFormDatas((prev) => [
          ...prev.filter((f) => f.stopeId !== updatedFormData.stopeId),
          updatedFormData,
        ]);
        if (prevRoute !== undefined) model.setSelectedRoute?.(prevRoute);
      }

      mutation?.reset();
      return undefined;
    }).filter((failure): failure is AutoRecipeFailure => !!failure);

    setAutoRecipeFailures(failures);
  };

  React.useEffect(() => {
    if (isAutoRecipe) return;

    if (!isValid || model.project.fillType !== FillType.Hydraulic) return;
    const results = triggerHydraulicFillStrengthFromRecipe(
      model,
      values.hydraulicSpecification?.binderTailingsRatio || 0.02,
      values.hydraulicSelectedSpecification?.massConcentration || limits.concentration.min,
      selectedRheologyDataset?.tailingsDrySolidsDensity,
      selectedRheologyDataset?.binderDryParticleDensity ?? 0,
      selectedRheologyDataset?.carrierFluidDensity ?? 0,
    );
    if (!results) return;
    setFieldValue('hydraulicOptimisedSpecification.settlingVelocity', results.settlingVelocity);
    setFieldValue('hydraulicOptimisedSpecification.minVelocity', results.minVelocity);
    setFieldValue('hydraulicOptimisedSpecification.maxPipePressure', results.maxPipePressure);
  }, [values.hydraulicSpecification, isValid, model.project.fillType, isAutoRecipe, selectedRheologyDataset, limits]);

  React.useEffect(() => {
    setFieldValue('pourPresetId', selectedPourPreset?.entityId);
    if (selectedPourPreset?.curingDays) {
      setFieldValue('pasteSpecification.targetDays', selectedPourPreset.curingDays);
    }
    if (selectedPourPreset?.ucsStrength) {
      setFieldValue('pasteSpecification.targetUCSStrength', selectedPourPreset.ucsStrength);
    }
  }, [selectedPourPreset]);

  React.useEffect(() => {
    if (isAutoRecipe) return;
    const current = model.getSelectedRoute();
    if (current && !isRouteActive(current)) return;
    setFieldValue('spools', current?.routePointStrings ?? []);
    setFieldValue('routeSpools', current?.spoolIds ?? []);
  }, [model.routes, isAutoRecipe]);

  React.useEffect(() => {
    if (skipWarningEffectRef.current) {
      skipWarningEffectRef.current = false;
      return;
    }
    let currentWarnings = checkForWarnings();
    if ((values.pasteSpecification?.targetUCSStrength || 0) > (values.pasteSelectedSpecification?.ucsStrength || 0)) {
      currentWarnings = [
        ...currentWarnings,
        { code: 'FormUcsStrengthBelowTarget', display: i18n._('UCS STRENGTH BELOW TARGET') },
      ];
    }

    if (!equals(currentWarnings, values.warnings) && isOptimised) {
      onWarning(currentWarnings);
      setFieldValue('warnings', currentWarnings);
    }
  }, [values.pasteSelectedSpecification, values.hydraulicSelectedSpecification]);

  React.useEffect(() => {
    setFieldValue('rheologyDataSetId', selectedRheologyDataset?.rheologyDataSetId);
  }, [selectedRheologyDataset]);

  React.useEffect(() => {
    if (optimisationError && optimisationError !== OptimisationErrorType.OTHER) {
      formik.validateForm().then(() => setOptimisationError(OptimisationErrorType.NONE));
    }
  }, [optimisationError]);

  const baseInputsProps = {
    unitSystemPreference: model.project.unitSystemPreference,
    throughputControlType,
    customUnitFields: model.project.customUnitFields,
    projectId,
    stopeId: stopeId[0],
    values,
    limits,
    classes,
    setFieldValue,
    setFieldTouched,
    hasError: helpers.hasError,
    getErrorHelpText: helpers.getErrorHelpText,
  };

  const targetInputsProps = {
    ...baseInputsProps,
    isOptimised,
    isSubmitting,
    isValid,
    ucsCoefficients,
    hasUcsData,
    maxPumpPressure,
    metricPressureUnit,
    handleBlur,
    onReset: handleReset,
    onTargetsUpdate: model.project.fillType === FillType.Paste ? handlePasteOptimise : handleHydraulicOptimise,
  };

  const actionProps = {
    isOptimised,
    isUpdate: !!recipe,
    isSubmitting,
    isValid,
    resetForm: () => {
      resetForm();
      updateIsOptimised(initialIsOptimisedState.current);
      setAutoRecipeFailures([]);
      setFormDatas([]);
    },
    dirty,
    onCancel,
    classes,
  };

  const displayUnitResults = useDisplayUnits([
    {
      key: 'adjusted',
      displayUnitPreferences: model.project.displayUnitPreferences,
      specification:
        model.project.fillType === FillType.Paste ? values.pasteSpecification : values.hydraulicSpecification,
      selectedSpecification:
        model.project.fillType === FillType.Paste
          ? values.pasteSelectedSpecification
          : values.hydraulicSelectedSpecification,
      specificGravity: selectedRheologyDataset?.tailingsDrySolidsDensity,
      yieldStressA:
        selectedRheologyDataset?.yieldStressCoefficients && Object.values(selectedRheologyDataset.yieldStressCoefficients)[0]
          ? Object.values(selectedRheologyDataset.yieldStressCoefficients)[0]
          : undefined,
      yieldStressB:
        selectedRheologyDataset?.yieldStressCoefficients && Object.values(selectedRheologyDataset.yieldStressCoefficients)[1]
          ? Object.values(selectedRheologyDataset.yieldStressCoefficients)[1]
          : undefined,
      mixerCoefficients: selectedRheologyDataset?.mixerCoefficients,
      heightOfCylinder: selectedRheologyDataset?.heightOfCylinder,
      fillType: model.project.fillType,
      throughputControlType,
      tailingsSolidsDensity: selectedRheologyDataset?.tailingsDrySolidsDensity ?? 0,
      carrierFluidDensity: selectedRheologyDataset?.carrierFluidDensity ?? 0,
      binderParticleDensity: selectedRheologyDataset?.binderDryParticleDensity ?? 0,
      limits,
    },
  ]);

  const { additionalDisplayUnits: additionalAdjustedDisplayUnitMap } = displayUnitResults.adjusted;

  return (
    <StyledBox sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box sx={{ width: '100%' }}>
        <form className={classes.form} onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <RheologyDataSetSelector
              mineModelId={projectId}
              rheologyDatasetList={model.rheology}
              selectedRheologyDatasetId={selectedRheologyDataset?.rheologyDataSetId}
              disabled={isOptimised}
              onRheologyDataSetSelected={(newRheologyData: RheologyDataDto | undefined) => handleRheologyDatasetSelected(newRheologyData)}
            />
          </Box>

          {model.project.fillType === FillType.Paste && !!pourPresetGroupId && model.rheology?.length > 0 && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <PourPresetField
                pourPresetGroupId={pourPresetGroupId}
                onChange={handlePourPresetSelected}
              />
            </Box>
          )}

          {model.project.fillType === FillType.Paste ? (
            <>
              <PasteTargetInputs
                targetDaysCoefficients={selectedRheologyDataset?.ucsCoefficients}
                pumpType={model.project.pumpType}
                optimisationError={optimisationError}
                {...targetInputsProps}
              />
              <NominalParametersSection
                value={nominalParams}
                onChange={handleNominalParamsChange}
                pipes={model.pipes}
                disabled={isOptimised}
              />
              {canEditTargetLines && (
                <TargetLinesSection
                  inputs={targetLineInputs}
                  onChange={setTargetLineInputs}
                  pipes={model.pipes}
                  disabled={isOptimised}
                  metricPressureUnit={metricPressureUnit}
                  unitSystemPreference={model.project.unitSystemPreference}
                  throughputControlType={throughputControlType}
                  allowedTargetTypes={allowedTargetTypes}
                />
              )}
              <Box sx={{ pt: 1, pb: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={isSubmitting || !isValid}
                  onClick={(e: { preventDefault: () => void }) => {
                    e.preventDefault();
                    isOptimised ? handleReset() : handlePasteOptimise();
                  }}
                >
                  {isOptimised ? <Trans id="Re-optimise">Re-optimise</Trans> : <Trans id="Optimise">Optimise</Trans>}
                </Button>
              </Box>
            </>
          ) : (
            <HydraulicTargetInputs {...targetInputsProps} />
          )}

          {isOptimised && !isAutoRecipe && (
            <>
              {model.project.fillType === FillType.Paste ? (
                <PasteAdjustedInputs
                  targetDaysCoefficients={selectedRheologyDataset?.ucsCoefficients}
                  pumpType={model.project.pumpType}
                  model={model}
                  {...targetInputsProps}
                />
              ) : (
                <HydraulicAdjustedInputs {...targetInputsProps} />
              )}

              <AdditionalDisplayUnits
                additionalDisplayUnitsMap={additionalAdjustedDisplayUnitMap}
                warnings={{} as Record<DisplayUnits, string | undefined>}
                unitSystemPreference={model.project.unitSystemPreference}
                customUnitFields={model.project.customUnitFields}
              />

              {model.project.fillType === FillType.Paste && (() => {
                const boosterPoints = getPointsWithBoosterPressure(selectedRoute);
                if (boosterPoints.length === 0) return null;
                return (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      <Trans id="Booster Pump Pressure">Booster Pump Pressure</Trans>
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {boosterPoints.map((routePoint) => (
                        <Box key={routePoint.point.id} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" sx={{ minWidth: 80 }}>
                            {routePoint.point.id}
                          </Typography>
                          <UnitSystemNumberField
                            unitSystem={model.project.unitSystemPreference}
                            customUnitFields={model.project.customUnitFields}
                            unitInfo={pumpPressureUnitInfo}
                            decimalPlaces={pumpPressureDecimalPlaces}
                            disabled
                            size="small"
                            value={round(
                              model.project.pumpType === PumpType.Pump
                                ? toDisplayPumpPressureBar(pumpPressureKpaToBar(routePoint.pressure ?? 0))
                                : pumpPressureBarToGravityMetricHead(pumpPressureKpaToBar(routePoint.pressure ?? 0)),
                              pumpPressureDecimalPlaces,
                            )}
                            label={i18n._('Pressure')}
                          />
                        </Box>
                      ))}
                    </Box>
                  </>
                );
              })()}

              {model.project.fillType === FillType.Paste
                && (values.pasteSlackFlowSpecification || values.pasteMaxPressureSpecification || targetLineResults.length > 0)
                && (() => {
                const hasUcsTarget = !!(values.pasteSpecification?.targetUCSStrength);
                const yieldCoeff1 = selectedDataset.yieldStressCoefficients.coefficient1;
                const yieldCoeff2 = selectedDataset.yieldStressCoefficients.coefficient2;
                return (
                  <>
                    <Divider sx={{ my: 2 }} />
                    {values.pasteSlackFlowSpecification && (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          <Trans id="Slack Flow">Slack Flow</Trans>
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                          <Box sx={{ width: '49%', pb: 2 }}>
                            <UnitSystemNumberField
                              unitSystem={model.project.unitSystemPreference}
                              customUnitFields={model.project.customUnitFields}
                              unitInfo={pumpPressureUnitInfo}
                              decimalPlaces={pumpPressureDecimalPlaces}
                              disabled
                              size="small"
                              value={round(
                                model.project.pumpType === PumpType.Pump
                                  ? toDisplayPumpPressureBar(values.pasteSlackFlowSpecification.pumpPressure ?? 0)
                                  : pumpPressureBarToGravityMetricHead(values.pasteSlackFlowSpecification.pumpPressure ?? 0),
                                pumpPressureDecimalPlaces,
                              )}
                              label={i18n._('Pump Pressure')}
                            />
                          </Box>
                          <Box sx={{ width: '49%', pb: 2 }}>
                            <ProjectNumberField
                              size="small"
                              fullWidth
                              decimalPlaces={2}
                              id="slack-flow-mass-concentration"
                              name="slack-flow-mass-concentration"
                              value={values.pasteSlackFlowSpecification.massConcentration ?? 0}
                              onChange={(parsed) => handleScenarioMassConcentrationChange('pasteSlackFlowSpecification', parsed)}
                              onBlur={() => setFieldTouched('pasteSlackFlowSpecification.massConcentration', true, true)}
                              error={helpers.hasError('pasteSlackFlowSpecification.massConcentration')}
                              helperText={helpers.getErrorHelpText('pasteSlackFlowSpecification.massConcentration')}
                              label={i18n._('Mass Conc.')}
                              slotProps={{
                                input: {
                                  endAdornment: <InputAdornment position="end">%m</InputAdornment>,
                                },
                              }}
                            />
                          </Box>
                          {hasUcsTarget && (
                            <Box sx={{ width: '49%', pb: 2 }}>
                              <TextField
                                disabled
                                size="small"
                                type="number"
                                fullWidth
                                value={round(values.pasteSlackFlowSpecification.binderContent ?? 0, 2)}
                                label={i18n._('Binder')}
                                slotProps={{
                                  input: {
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                  },
                                }}
                              />
                            </Box>
                          )}
                          <Box sx={{ width: '49%', pb: 2 }}>
                            <TextField
                              disabled
                              size="small"
                              type="number"
                              fullWidth
                              value={round(calculateYieldfromConcentrationByMass(
                                values.pasteSlackFlowSpecification.massConcentration ?? 0,
                                yieldCoeff1,
                                yieldCoeff2,
                              ), 1)}
                              label={i18n._('Yield Stress')}
                              slotProps={{
                                input: {
                                  endAdornment: <InputAdornment position="end">Pa</InputAdornment>,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </>
                    )}
                    {values.pasteMaxPressureSpecification && (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          <Trans id="Max Pressure">Max Pressure</Trans>
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                          <Box sx={{ width: '49%', pb: 2 }}>
                            <UnitSystemNumberField
                              unitSystem={model.project.unitSystemPreference}
                              customUnitFields={model.project.customUnitFields}
                              unitInfo={pumpPressureUnitInfo}
                              decimalPlaces={pumpPressureDecimalPlaces}
                              disabled
                              size="small"
                              value={round(
                                model.project.pumpType === PumpType.Pump
                                  ? toDisplayPumpPressureBar(values.pasteMaxPressureSpecification.pumpPressure ?? 0)
                                  : pumpPressureBarToGravityMetricHead(values.pasteMaxPressureSpecification.pumpPressure ?? 0),
                                pumpPressureDecimalPlaces,
                              )}
                              label={i18n._('Pump Pressure')}
                            />
                          </Box>
                          <Box sx={{ width: '49%', pb: 2 }}>
                            <ProjectNumberField
                              size="small"
                              fullWidth
                              decimalPlaces={2}
                              id="max-pressure-mass-concentration"
                              name="max-pressure-mass-concentration"
                              value={values.pasteMaxPressureSpecification.massConcentration ?? 0}
                              onChange={(parsed) => handleScenarioMassConcentrationChange('pasteMaxPressureSpecification', parsed)}
                              onBlur={() => setFieldTouched('pasteMaxPressureSpecification.massConcentration', true, true)}
                              error={helpers.hasError('pasteMaxPressureSpecification.massConcentration')}
                              helperText={helpers.getErrorHelpText('pasteMaxPressureSpecification.massConcentration')}
                              label={i18n._('Mass Conc.')}
                              slotProps={{
                                input: {
                                  endAdornment: <InputAdornment position="end">%m</InputAdornment>,
                                },
                              }}
                            />
                          </Box>
                          {hasUcsTarget && (
                            <Box sx={{ width: '49%', pb: 2 }}>
                              <TextField
                                disabled
                                size="small"
                                type="number"
                                fullWidth
                                value={round(values.pasteMaxPressureSpecification.binderContent ?? 0, 2)}
                                label={i18n._('Binder')}
                                slotProps={{
                                  input: {
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                  },
                                }}
                              />
                            </Box>
                          )}
                          <Box sx={{ width: '49%', pb: 2 }}>
                            <TextField
                              disabled
                              size="small"
                              type="number"
                              fullWidth
                              value={round(calculateYieldfromConcentrationByMass(
                                values.pasteMaxPressureSpecification.massConcentration ?? 0,
                                yieldCoeff1,
                                yieldCoeff2,
                              ), 1)}
                              label={i18n._('Yield Stress')}
                              slotProps={{
                                input: {
                                  endAdornment: <InputAdornment position="end">Pa</InputAdornment>,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </>
                    )}
                    {targetLineResults.map((result, index) => {
                      if (!result.result) {
                        return result.hasErrors ? (
                          // eslint-disable-next-line react/no-array-index-key
                          <Box key={index} sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'error.main' }}>
                              {`Target ${index + 1} - ${result.errorDescription || i18n._('No solution found')}`}
                            </Typography>
                          </Box>
                        ) : null;
                      }
                      const input = targetLineInputs[index];
                      const isFlowRateTarget = input?.targetType === OptimisationTargetType.FlowRate;
                      const pipeName = input?.targetPipeId ? (model.pipes[input.targetPipeId]?.name || input.targetPipeId) : '';
                      const headerLabel = (() => {
                        if (input?.targetType === OptimisationTargetType.KPaPerM) {
                          const targetDisplay = input.kPaPerMTarget === undefined ? '' : toDisplayFrictionLossTarget(input.kPaPerMTarget);
                          return `${targetDisplay} ${frictionLossAbbreviation}${pipeName ? ` ${pipeName}` : ''}`;
                        }
                        if (input?.targetType === OptimisationTargetType.PumpPressure) {
                          const targetDisplay = input.pumpPressureTarget === undefined ? '' : toDisplayPumpPressureTarget(input.pumpPressureTarget);
                          return `${targetDisplay} ${pumpPressureTargetAbbreviation}`;
                        }
                        if (input?.targetType === OptimisationTargetType.Yield) {
                          return `${input.yieldTarget ?? ''} ${i18n._('Pa')}`;
                        }
                        if (input?.targetType === OptimisationTargetType.FlowRate) {
                          const targetDisplay = input.flowRateTarget === undefined ? '' : round(toDisplayFlowRate(input.flowRateTarget), 1);
                          return `${targetDisplay} ${flowRateAbbreviation}`;
                        }
                        return `Target ${index + 1}`;
                      })();
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <React.Fragment key={index}>
                          <Typography variant="subtitle2" sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}>
                            {headerLabel}
                            {result.hasErrors && result.errorDescription && (
                              <Typography component="span" variant="body2" color="warning.main" sx={{ ml: 1 }}>
                                {`(${result.errorDescription})`}
                              </Typography>
                            )}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                            <Box sx={{ width: '49%', pb: 2 }}>
                              <UnitSystemNumberField
                                unitSystem={model.project.unitSystemPreference}
                                customUnitFields={model.project.customUnitFields}
                                unitInfo={model.project.pumpType === PumpType.Pump ? pumpPressureUnitInfo : unitLabels[BasicDisplayUnits.Gravity]}
                                decimalPlaces={model.project.pumpType === PumpType.Pump && isKiloPascalPressure ? 0 : 1}
                                disabled
                                size="small"
                                value={model.project.pumpType === PumpType.Pump
                                  ? round(
                                    isKiloPascalPressure
                                      ? result.result.actualPumpPressureKpa
                                      : pumpPressureKpaToBar(result.result.actualPumpPressureKpa),
                                    isKiloPascalPressure ? 0 : 1,
                                  )
                                  : round(pumpPressureBarToGravityMetricHead(pumpPressureKpaToBar(result.result.actualPumpPressureKpa)), 1)}
                                label={i18n._('Pump Pressure')}
                              />
                            </Box>
                            <Box sx={{ width: '49%', pb: 2 }}>
                              <ProjectNumberField
                                size="small"
                                fullWidth
                                decimalPlaces={2}
                                // A flow rate target inherits the optimised recipe's mix; edit it there, not here.
                                disabled={isFlowRateTarget}
                                id={`target-line-mass-concentration-${index}`}
                                name={`target-line-mass-concentration-${index}`}
                                value={round(result.result.concentrationByMass * 100, 2)}
                                onChange={(parsed) => handleTargetLineMassConcentrationChange(index, parsed)}
                                label={i18n._('Mass Conc.')}
                                slotProps={{
                                  input: {
                                    endAdornment: <InputAdornment position="end">%m</InputAdornment>,
                                  },
                                }}
                              />
                            </Box>
                            {hasUcsTarget && !isFlowRateTarget && (
                              <Box sx={{ width: '49%', pb: 2 }}>
                                <TextField
                                  disabled
                                  size="small"
                                  type="number"
                                  fullWidth
                                  value={round(result.result.cement * 100, 2)}
                                  label={i18n._('Binder')}
                                  slotProps={{
                                    input: {
                                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    },
                                  }}
                                />
                              </Box>
                            )}
                            {!isFlowRateTarget && (
                              <Box sx={{ width: '49%', pb: 2 }}>
                                <TextField
                                  disabled
                                  size="small"
                                  type="number"
                                  fullWidth
                                  value={round(calculateYieldfromConcentrationByMass(
                                    result.result.concentrationByMass * 100,
                                    yieldCoeff1,
                                    yieldCoeff2,
                                  ), 1)}
                                  label={i18n._('Yield Stress')}
                                  slotProps={{
                                    input: {
                                      endAdornment: <InputAdornment position="end">Pa</InputAdornment>,
                                    },
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </React.Fragment>
                      );
                    })}
                  </>
                );
              })()}
            </>
          )}

          {isAutoRecipe && autoRecipeFailures.length > 0 && (
            <Alert severity={formDatas.length > 0 ? 'warning' : 'error'} sx={{ mb: 1 }}>
              <Typography variant="body2">
                {formDatas.length > 0
                  ? i18n._('{optimised} of {total} stopes optimised. No recipe could be created for:', {
                    optimised: formDatas.length,
                    total: stopeId.length,
                  })
                  : i18n._('No recipe could be created for any of the selected stopes:')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {autoRecipeFailures.map((failure) => (
                  <Box component="li" key={failure.stopeId}>
                    <Typography variant="caption">
                      {`${failure.stopeName}: ${describeFailureReason(failure.reason)}`}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Alert>
          )}

          {isOptimised && isAutoRecipe && autoRecipeFailures.length === 0 && (
            <Box sx={{ textAlign: 'center', color: yellow[700] }}>
              <Trans id="All Recipes Optimised Successfully!">
                All Recipes Optimised Successfully!
              </Trans>
            </Box>
          )}

          <FormActions {...actionProps} />
        </form>
      </Box>
    </StyledBox>
  );
};

export default RecipeForm;
